import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, MailIcon, PhoneIcon, ClockIcon, CheckIcon } from 'lucide-react';
import { useScrollFadeIn } from '../hooks/useScrollFadeIn';
interface CTASectionProps {
  exerciseComplete?: boolean;
}
export function CTASection({
  exerciseComplete = false
}: CTASectionProps) {
  const {
    ref,
    isVisible
  } = useScrollFadeIn();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    time: 'Morning'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  return <section id="booking" className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-b from-white to-[#E8F8F9] flex flex-col justify-center" data-id="element-30">
      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 20
    }} animate={isVisible ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.6
    }} className="max-w-5xl mx-auto w-full" data-id="element-31">
        <h2 className="text-navy text-2xl md:text-4xl lg:text-5xl font-bold text-center mb-8 md:mb-12" data-id="element-32">
          Ready to Go Deeper?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10 md:mb-16" data-id="element-33">
          {/* Card 1: Booking Form */}
          <div className="bg-white rounded-2xl shadow-xl p-5 md:p-8 border border-gray-100" data-id="element-34">
            <h3 className="text-navy text-lg md:text-xl font-bold mb-4 md:mb-6" data-id="element-35">
              Book a Free 15-Minute Exploratory Session
            </h3>

            {!exerciseComplete ? <div className="flex flex-col items-center justify-center text-center py-8 space-y-4" data-id="element-36">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-37">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-38">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-39" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-40" />
                  </svg>
                </div>
                <p className="text-navy font-semibold text-lg" data-id="element-41">
                  Complete the image exercise first
                </p>
                <p className="text-gray-500 text-sm max-w-xs" data-id="element-42">
                  Take the 2-minute exercise above to unlock your personalized
                  session booking.
                </p>
                <button onClick={() => document.getElementById('image-exercise')?.scrollIntoView({
              behavior: 'smooth'
            })} className="text-teal font-semibold text-sm hover:underline mt-1" data-id="element-43">
                  ↑ Start the Exercise
                </button>
              </div> : isSubmitted ? <div className="flex flex-col items-center justify-center text-center py-8 space-y-4" data-id="element-44">
                <div className="w-16 h-16 bg-lightteal rounded-full flex items-center justify-center mb-2" data-id="element-45">
                  <CheckIcon className="w-8 h-8 text-teal" data-id="element-46" />
                </div>
                <h4 className="text-xl font-semibold text-navy" data-id="element-47">Thank you!</h4>
                <p className="text-gray-600" data-id="element-48">
                  A LiveWright coach will be in touch within 24 hours.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-49">
                <div className="relative" data-id="element-50">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-51">
                    <UserIcon className="h-5 w-5 text-gray-400" data-id="element-52" />
                  </div>
                  <input type="text" required placeholder="Your Name" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="pl-10 w-full rounded-lg border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-teal/50 placeholder:text-gray-500" data-id="element-53" />
                </div>

                <div className="relative" data-id="element-54">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-55">
                    <MailIcon className="h-5 w-5 text-gray-400" data-id="element-56" />
                  </div>
                  <input type="email" required placeholder="Email Address" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="pl-10 w-full rounded-lg border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-teal/50 placeholder:text-gray-500" data-id="element-57" />
                </div>

                <div className="relative" data-id="element-58">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-59">
                    <PhoneIcon className="h-5 w-5 text-gray-400" data-id="element-60" />
                  </div>
                  <input type="tel" required placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} className="pl-10 w-full rounded-lg border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-teal/50 placeholder:text-gray-500" data-id="element-61" />
                </div>

                <div className="relative" data-id="element-62">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-63">
                    <ClockIcon className="h-5 w-5 text-gray-400" data-id="element-64" />
                  </div>
                  <select value={formData.time} onChange={e => setFormData({
                ...formData,
                time: e.target.value
              })} className="pl-10 pr-10 w-full rounded-lg border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-teal/50 bg-white appearance-none" data-id="element-65">
                    <option value="Morning" data-id="element-66">Morning</option>
                    <option value="Afternoon" data-id="element-67">Afternoon</option>
                    <option value="Evening" data-id="element-68">Evening</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-69">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-70">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-71" />
                    </svg>
                  </div>
                </div>

                <button type="submit" className="w-full bg-teal text-white rounded-lg py-3 font-semibold hover:bg-teal/90 transition-colors mt-2" data-id="element-72">
                  Book My Session
                </button>
              </form>}
          </div>

          {/* Card 2: LiveMORE Info */}
          <div className="bg-white rounded-2xl shadow-xl p-5 md:p-8 border border-gray-100" data-id="element-73">
            <h3 className="text-navy text-lg md:text-xl font-bold mb-4" data-id="element-74">
              Learn About LiveMORE
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-75">
              A year-long transformational coaching program with Bob and Judith
              Wright. 5 weekend intensives, weekly coaching, and a community of
              high-achieving professionals.
            </p>

            <ul className="text-gray-600 text-sm space-y-2 mb-6" data-id="element-76">
              <li className="flex items-start gap-2" data-id="element-77">
                <CheckIcon className="w-4 h-4 text-teal mt-0.5 shrink-0" data-id="element-78" />
                <span data-id="element-79">5 immersive weekend intensives throughout the year</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-80">
                <CheckIcon className="w-4 h-4 text-teal mt-0.5 shrink-0" data-id="element-81" />
                <span data-id="element-82">
                  Weekly 1-on-1 coaching with a certified Wright coach
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-83">
                <CheckIcon className="w-4 h-4 text-teal mt-0.5 shrink-0" data-id="element-84" />
                <span data-id="element-85">
                  A supportive community of high-achieving professionals
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-86">
                <CheckIcon className="w-4 h-4 text-teal mt-0.5 shrink-0" data-id="element-87" />
                <span data-id="element-88">
                  Backed by 30+ years of research in human development
                </span>
              </li>
            </ul>

            <div className="flex flex-wrap gap-2 mb-6" data-id="element-89">
              <span className="inline-flex rounded-full px-4 py-1 text-sm font-medium bg-amber-100 text-amber-800" data-id="element-90">
                Bronze
              </span>
              <span className="inline-flex rounded-full px-4 py-1 text-sm font-medium bg-gray-200 text-gray-700" data-id="element-91">
                Silver
              </span>
              <span className="inline-flex rounded-full px-4 py-1 text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-92">
                Gold
              </span>
              <span className="inline-flex rounded-full px-4 py-1 text-sm font-medium bg-slate-200 text-slate-700" data-id="element-93">
                Platinum
              </span>
            </div>

            <button className="w-full border-2 border-navy text-navy rounded-lg py-3 font-semibold hover:bg-navy hover:text-white transition-colors" data-id="element-94">
              Explore LiveMORE
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mb-10 md:mb-16" data-id="element-95">
          <h3 className="text-navy text-lg font-bold text-center mb-6" data-id="element-96">
            What People Are Saying
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-97">
            <div className="flex flex-col items-center text-center" data-id="element-98">
              <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Sarah M." className="w-14 h-14 rounded-full object-cover mb-3 shadow-sm" data-id="element-99" />
              <p className="italic text-gray-500 text-sm leading-relaxed mb-2" data-id="element-100">
                "LiveMORE was the most significant personal development
                experience of my life."
              </p>
              <p className="text-navy font-semibold text-sm" data-id="element-101">Sarah M.</p>
              <p className="text-gray-400 text-xs" data-id="element-102">LiveMORE Graduate</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-103">
              <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="James R." className="w-14 h-14 rounded-full object-cover mb-3 shadow-sm" data-id="element-104" />
              <p className="italic text-gray-500 text-sm leading-relaxed mb-2" data-id="element-105">
                "I finally understand what's been driving me — and what's been
                holding me back."
              </p>
              <p className="text-navy font-semibold text-sm" data-id="element-106">James R.</p>
              <p className="text-gray-400 text-xs" data-id="element-107">Executive Coach Client</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-108">
              <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="Maria L." className="w-14 h-14 rounded-full object-cover mb-3 shadow-sm" data-id="element-109" />
              <p className="italic text-gray-500 text-sm leading-relaxed mb-2" data-id="element-110">
                "The yearnings exercise opened my eyes. I recommend this to
                everyone I know."
              </p>
              <p className="text-navy font-semibold text-sm" data-id="element-111">Maria L.</p>
              <p className="text-gray-400 text-xs" data-id="element-112">
                Weekend Intensive Attendee
              </p>
            </div>
          </div>
        </div>

        <div className="text-center" data-id="element-113">
          <div className="w-16 h-0.5 bg-teal mx-auto mb-4" data-id="element-114" />
          <p className="text-xs text-gray-500" data-id="element-115">
            Powered by AgileBrain + Knowledge Spaces
          </p>
        </div>
      </motion.div>
    </section>;
}